import React, { ReactNode } from 'react';

import controlTabStyles from './styles.module.css';
import { DataTableColumn } from '@/components/DataTable';

export interface TableRow {
  label: string;
  value: string | React.ReactNode;
  key: string;
  unit?: string;
  max?: string;
  min?: string;
}

export interface StatusIndicatorProps {
  status: '0' | '1' | Iterable<ReactNode>;
  styles: {
    statusIndicator: string;
    statusOn: string;
    statusOff: string;
  };
}

export const StatusIndicator = ({ status, styles }: StatusIndicatorProps) => {
  return (
    <div className={`${styles.statusIndicator} status-indicator-wrapper`}>
      {status === '0' ? (
        <div className={styles.statusOff}>Off</div>
      ) : (
        <div className={styles.statusOn}>On</div>
      )}
    </div>
  );
};

export const createStatusIndicatorWithStyles = (styles: {
  statusIndicator: string;
  statusOn: string;
  statusOff: string;
}) => {
  return ({ status }: { status: '0' | '1' }) => {
    return (
      <StatusIndicator
        status={status}
        styles={{
          statusIndicator: styles.statusIndicator,
          statusOn: styles.statusOn,
          statusOff: styles.statusOff
        }}
      />
    );
  };
};

export const StatusIndicatorWithStyles = createStatusIndicatorWithStyles({
  statusIndicator: controlTabStyles.statusIndicator,
  statusOn: controlTabStyles.statusOn,
  statusOff: controlTabStyles.statusOff
});

export const createSequenceColumns = (
  StatusIndicatorComponent: React.ComponentType<{ status: '0' | '1' }>
): DataTableColumn<TableRow>[] => [
  {
    field: 'label',
    headerName: 'Parameter',
    width: 'auto',
    alignLeft: true,
    renderCell: ({ row }) => (
      <span style={{ fontWeight: '500', fontSize: '18px' }}>{row.label}</span>
    )
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 'auto',
    alignCenter: true,
    renderCell: ({ row }) => {
      if (row.unit) {
        return (
          <span style={{ color: 'var(--main-blue-color)' }}>{row.value}</span>
        );
      }
    }
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 100,
    alignCenter: true,
    needPadding: row => row.value !== '0' && row.value !== '1',
    renderCell: ({ row }) => {
      if (row.unit) {
        return `${row.unit}`;
      }
      if (row.value === '0' || row.value === '1') {
        return <StatusIndicatorComponent status={row.value as '0' | '1'} />;
      }
      return row.value;
    }
  }
];

export const createGeneratorColumns = (
  StatusIndicatorComponent: React.ComponentType<{ status: '0' | '1' }>
): DataTableColumn<TableRow>[] => [
  {
    field: 'label',
    headerName: 'Parameter',
    width: 'auto',
    alignLeft: true,
    renderCell: ({ row }) => (
      <span style={{ fontWeight: '500', fontSize: '18px' }}>{row.label}</span>
    )
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 'auto',
    alignCenter: true,
    renderCell: ({ row }) => {
      if (row.unit) {
        return (
          <span style={{ color: 'var(--main-blue-color)' }}>{row.value}</span>
        );
      }
    }
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 100,
    alignCenter: true,
    needPadding: row => row.value !== '0' && row.value !== '1',
    renderCell: ({ row }) => {
      if (row.value === '0' || row.value === '1') {
        return <StatusIndicatorComponent status={row.value as '0' | '1'} />;
      }
      if (row.unit) {
        return <span>{row.unit}</span>;
      }
    }
  }
];

export const createControlSystemColumns = (
  StatusIndicatorComponent: React.ComponentType<{ status: '0' | '1' }>
): DataTableColumn<TableRow>[] => [
  {
    field: 'label',
    headerName: 'Parameter',
    width: 'auto',
    alignLeft: true,
    renderCell: ({ row }) => (
      <span style={{ fontWeight: '500', fontSize: '18px' }}>{row.label}</span>
    )
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 100,
    alignCenter: true,
    renderCell: ({ row }) => {
      if (row.unit) {
        return (
          <span style={{ color: 'var(--main-blue-color)' }}>{row.value}</span>
        );
      }
    }
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 100,
    alignCenter: true,
    needPadding: row =>
      row.value !== '0' && row.value !== '1' && row.unit !== '',
    renderCell: ({ row }) => {
      if ((row.value === '0' || row.value === '1') && row.unit === '') {
        return <StatusIndicatorComponent status={row.value as '0' | '1'} />;
      }
      if (row.unit) {
        return row.unit;
      }
      return row.value;
    }
  }
];

export const createSequenceRows = (initialData: any): TableRow[] => {
  if (!initialData?.sequence) return [];
  return initialData.sequence as TableRow[];
};

export const createGeneratorRows = (initialData: any): TableRow[] => {
  if (!initialData?.generator) return [];
  return initialData.generator as TableRow[];
};

export const createControlSystemRows = (initialData: any): TableRow[] => {
  if (!initialData?.controlSystem) return [];
  return initialData.controlSystem as TableRow[];
};
